import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { STORAGE_KEYS } from '@constants/storageKeys';

function cancelSmoothScroll() {
  if (typeof window === 'undefined' || typeof window.scrollTo !== 'function') {
    return;
  }

  window.scrollTo(window.scrollX, window.scrollY);
}

/**
 * Hook to auto-scroll to active transcript segment
 */
export function useAutoScroll(activeKey) {
  const [enabled, setEnabled] = useLocalStorage(
    STORAGE_KEYS.AUTO_SCROLL_ENABLED,
    true // Enabled by default
  );
  const [isPaused, setIsPaused] = useState(false);

  const activeElementRef = useRef(null);
  const autoScrollingRef = useRef(false);
  const autoScrollTimeoutRef = useRef(null);

  // Manual scrolling pauses automatic movement so reading is not interrupted.
  useEffect(() => {
    const handleScroll = () => {
      if (!autoScrollingRef.current) {
        setIsPaused(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to the active element when playback advances to another segment.
  useEffect(() => {
    if (!enabled || isPaused || activeKey == null || !activeElementRef.current) {
      return undefined;
    }

    autoScrollingRef.current = true;
    activeElementRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
    }

    autoScrollTimeoutRef.current = setTimeout(() => {
      autoScrollingRef.current = false;
    }, 800);

    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }
      autoScrollingRef.current = false;
    };
  }, [enabled, isPaused, activeKey]);

  // Stop native smooth scrolling after the transcript route is removed.
  useEffect(() => {
    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }

      autoScrollingRef.current = false;
      cancelSmoothScroll();
    };
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  return {
    enabled,
    setEnabled,
    activeElementRef,
    isPaused,
    resume
  };
}
